import { merge } from 'lodash';
import moment from 'utils/app-moment';
import { TicketType } from 'store/models/TicketTypeHelper';
import {
  EachHasLoaded,
  StoreHasLoadedI,
} from 'store/models/workdesk-tickets/HasLoadedHelper';
import { StatusCountI } from 'store/models/NumTickets';
import getInboxesFromSelectedInboxes from 'store/helpers/getInboxesFromSelectedInboxes';
import SearchFilter, {
  SearchValuesApi,
} from 'store/models/SearchFilter';
import { timeFormatters } from 'utils/time-formatters';
import GmailLabels from 'store/models/GmailLabels';
import EachWorkdeskTicket from 'store/models/EachWorkdeskTicket';
import { HasLoadedHelper } from 'store/models/workdesk-tickets';
import MatchHelper from 'store/models/MatchHelper';
import TicketTypeHelper from 'store/models/TicketTypeHelper';
import {
  RelatedObjI,
  Statuses,
  StatusI,
} from 'store/models/Statuses';
import { WorkdeskFilterWithPrevStoreState } from 'store/models/WorkdeskFilter';
import {
  MAX_LENGTH_TO_DOWNLOAD,
  ROWS_PER_STATUS,
} from 'config/workdesk-load';
import {
  AllWorkdeskTicketsI,
  AssigneeIdToInfo,
  EachWorkdeskTicketToShowI,
  IdBodyHtmlAndOtherResI,
  LoadWorkdeskTicketsParams,
  TicketsMap,
} from 'store/models/workdesk-tickets/workdesk-tickets.interface';
import { EachSortBy } from 'store/models/workdesk-tickets/workdesk-tickets.interface';
import { EachWorkdeskTicketI } from './each-workdesk-ticket.interface';
import { isNullOrUndefined } from 'utils';

export const MAX_LENGTH_TO_DOWNLOAD_PER_STATUS = Math.ceil(
  MAX_LENGTH_TO_DOWNLOAD / Statuses.mainStatuses.length,
);

export const ticketLabelColorMap = {
  fromWs: '#ffd70247',
  justChanged: `rgb(255 222 241 / 90%)`,
  changedButLoaded: 'rgb(255 222 241 / 70%)',
  justOpened: '#deebff',
};

const formatADiffB = (a, b) => {
  return a && b
    ? moment
        .utc(moment(a).diff(moment(b)))
        .format('HH[h ]mm[m ]ss[s]')
    : null;
};
export class WorkdeskTickets {
  ticketsMap: TicketsMap;
  assigneeIdToInfo;
  hasLoaded;
  constructor(ticketsMap, assigneeIdToInfo = {}, hasLoaded = {}) {
    this.ticketsMap = ticketsMap;
    this.assigneeIdToInfo = assigneeIdToInfo;
    this.hasLoaded = hasLoaded;
  }

  static _formatTicketsData(tickets: EachWorkdeskTicketI[]) {
    return tickets.map((ticket, index) => {
      const preprocessTurntime = formatADiffB(
        ticket.preprocessingDoneTime,
        ticket.receivedTime,
      );
      const overallTurnTime = formatADiffB(
        ticket.submitBookingDate,
        ticket.receivedTime,
      );
      const indexerTurnTime = formatADiffB(
        ticket.submitBookingDateInitial,
        ticket.userClickTime,
      );
      const formatted = new EachWorkdeskTicket(
        ticket,
      ).getFormattedDetail();
      return {
        ...ticket,
        ...formatted,
        labels: new GmailLabels(ticket.labels).getLabels(),
        rowNo: index + 1,
        preprocessTurntime,
        overallTurnTime,
        indexerTurnTime,
      };
    });
  }

  static formatTicketsDataForShow(
    tickets: EachWorkdeskTicketI[],
    assigneeIdToInfo,
    isJustChangedTicketIdMap,
    addressToInbox,
  ) {
    return WorkdeskTickets._formatTicketsData(tickets).map(
      (ticket) => {
        return {
          ...ticket,
          isJustChanged:
            (isJustChangedTicketIdMap &&
              isJustChangedTicketIdMap[ticket.id]) ??
            null,
          receivedTimeText: timeFormatters.getWorkdeskReceivedTimeText(
            ticket.receivedTime,
          ),
          receivedTimeHoverText: timeFormatters.getWorkdeskReceivedTimeHoverText(
            ticket.receivedTime,
          ),
          updateDateText: timeFormatters.getWorkdeskReceivedTimeText(
            ticket.updateDate,
          ),
          updateDateHoverText: timeFormatters.getWorkdeskReceivedTimeHoverText(
            ticket.updateDate,
          ),
          inbox:
            ticket.inboxAddress &&
            addressToInbox &&
            addressToInbox[ticket.inboxAddress],
          assignee: isNullOrUndefined(ticket.assigneeId)
            ? null
            : assigneeIdToInfo[ticket.assigneeId as number],
        };
      },
    );
  }

  /**
   * for now only used with the selector for showing tickets on the workdesk page
   * @param tickets
   * @param sortBy
   */
  static getSortedTicketsBySortBy(
    tickets: EachWorkdeskTicketToShowI[] | EachWorkdeskTicketI[],
    sortBy: EachSortBy[],
  ): EachWorkdeskTicketToShowI[] | EachWorkdeskTicketI[] {
    if (!sortBy) {
      // when loadParams is null (inboxes is null, sortBy can be udnefined)
      return tickets;
    }
    console.time('** TIME ** getSortedTicketsBySortBy');
    const sorted = [...tickets];
    console.debug(`** sort tickets ${sorted.length}`);
    // go through sort by
    sortBy.forEach((sortByItem: EachSortBy) => {
      sorted.sort((a, b) => {
        const first = a;
        const sec = b;
        const firstAtField = first[sortByItem.field];
        const secAtField = sec[sortByItem.field];
        const multiplier = sortByItem.dir === 'DESC' ? -1 : 1;
        let toConsider;
        if (
          ['receivedTime', 'updateDate'].includes(sortByItem.field)
        ) {
          toConsider =
            multiplier *
            moment(firstAtField).diff(moment(secAtField));
        } else {
          // console.warn(
          //   `do not have specific sort function for ${sortByItem.field}`,
          // );
          /* null will always come later */
          toConsider = multiplier * (firstAtField - secAtField);
        }
        return (
          (firstAtField === null ? 1 : 0) -
            (secAtField === null ? 1 : 0) || toConsider
        );
      });
    });
    console.timeEnd('** TIME ** getSortedTicketsBySortBy');
    // re row number
    return sorted.map((ticket, index) => ({
      ...ticket,
      rowNo: index + 1,
    }));
  }

  static headersForDownload = [
    'rowNo',
    'id',
    'status',
    'subject',
    'labels',
    'si',
    'bookingNos',
    'assigneeId',
    'assigneeName',
    'assigneeEmail',
    'inbox',
    'indexerId',
    'teamCode',
    'country',
    'receivedTime',
    'receivedInWebAgent', // 'creationTime',
    'preprocessTurntime',
    'assignTime',
    'sentInitial', // submitBookingDateInitial
    'sentLatest', // submitBookingDate
    'indexerTurnTime',
    'overallTurnTime',
    'updateDate',
    'preprocessingDoneTime',
  ];

  /**
   * at least for download csv
   * @param tickets
   */
  static deduplicate(tickets: EachWorkdeskTicketI[]) {
    const ticketIdMap = tickets.reduce(
      (acc, ticket) => ({ ...acc, [ticket.id]: ticket }),
      {},
    );
    const ticketIds = Array.from(
      new Set(tickets.map((ticket) => ticket.id)),
    );
    return ticketIds.map((id) => ticketIdMap[id]);
  }

  static formatTicketsDataForDownload(
    tickets: EachWorkdeskTicketI[],
    assigneeIdToInfo: AssigneeIdToInfo,
  ) {
    return WorkdeskTickets._formatTicketsData(tickets).map(
      (ticket) => ({
        ...ticket,

        status: ticket.statusText,
        labels: ticket.labels.map((label) => label.name),
        assigneeName:
          ticket.assigneeId !== null &&
          ticket.assigneeId !== undefined &&
          assigneeIdToInfo &&
          assigneeIdToInfo[ticket.assigneeId] &&
          assigneeIdToInfo[ticket.assigneeId].name,
        assigneeEmail:
          ticket.assigneeId !== null &&
          ticket.assigneeId !== undefined &&
          assigneeIdToInfo &&
          assigneeIdToInfo[ticket.assigneeId] &&
          assigneeIdToInfo[ticket.assigneeId].email,
      }),
    );
  }

  /**
   * put the newer one first
   * @param newTickets
   */
  mergeTickets(
    newTickets:
      | AllWorkdeskTicketsI
      | { id: string; status: string }[]
      | { id: string; bookingNo: string }[],
    fromWsAt?: string,
  ): {
    ticketsMap: {
      [id: string]: EachWorkdeskTicketI;
    };
    newTicketIds: string[];
    statusChangedIds: string[];
  } {
    // console.debug(`ticketsMap`, this.ticketsMap);

    const newTicketsMap: {
      [id: string]:
        | EachWorkdeskTicketI
        | { id: string; status: string };
    } = (newTickets as any).reduce((all, ticket) => {
      let thisTicket = ticket.status
        ? ticket
        : {
            ...(this.ticketsMap[ticket.id] || {}),
            ...ticket,
          };
      thisTicket.fromWs = !!fromWsAt || false;
      thisTicket.fromWsAt = fromWsAt;
      return {
        ...all,
        [ticket.id]: thisTicket,
      };
    }, {});
    const oldIds = Object.keys(this.ticketsMap);
    const newTicketIds = Object.keys(newTicketsMap).filter(
      (id) => !oldIds.includes(id),
    );

    // statusChangedIds from the old
    const statusChangedIds = Object.keys(this.ticketsMap).filter(
      (id) =>
        id in newTicketsMap &&
        id in this.ticketsMap &&
        this.ticketsMap[id].status !== newTicketsMap[id].status,
    );

    return {
      ticketsMap: WorkdeskTickets.mergeTicketMaps(
        this.ticketsMap,
        newTicketsMap,
      ),
      newTicketIds,
      statusChangedIds,
    };
  }

  static mergeTicketMaps(
    oldTicketMaps: TicketsMap,
    newTicketMaps: {
      [id: string]:
        | EachWorkdeskTicketI
        | { id: string; status: string };
    },
  ) {
    const result = Object.keys(newTicketMaps).reduce((acc, id) => {
      if (id in acc) {
        return {
          ...acc,
          [id]: { ...acc[id], ...newTicketMaps[id] },
        } as TicketsMap;
      } else {
        return { ...acc, [id]: newTicketMaps[id] } as TicketsMap;
      }
    }, oldTicketMaps);
    return result;
  }

  addBodyHtmlLinkToTicketsMap(
    idBodyHtmlResData: IdBodyHtmlAndOtherResI | [],
  ) {
    const idBodyHtmlMap = Object.keys(idBodyHtmlResData).reduce(
      (acc, id) => ({
        ...acc,
        [id]: idBodyHtmlResData[id],
      }),
      {},
    );
    return Object.entries(this.ticketsMap).reduce(
      (acc, [id, ticket]) => ({
        ...acc,
        [id]:
          id in idBodyHtmlMap
            ? {
                ...ticket,
                bodyHtmlLink: idBodyHtmlMap[id].bodyHtmlLink,
              }
            : ticket,
      }),
      {},
    );
  }

  static mergeAssigneeIdToInfo(
    oldAssigneeIdToInfo,
    newAssigneeIdToInfo,
  ) {
    const mergedResult = merge(
      {},
      oldAssigneeIdToInfo,
      newAssigneeIdToInfo,
    );
    return mergedResult;
  }

  mergeAssigneeIdToInfo(newAssigneeIdToInfo) {
    const mergedResult = merge(
      {},
      this.assigneeIdToInfo,
      newAssigneeIdToInfo,
    );
    return mergedResult;
  }

  /**
   * TODO: test case
   * @param tickets
   */
  static getRelatedObjWithTickets(
    tickets: AllWorkdeskTicketsI,
  ): RelatedObjI {
    const result = {};
    tickets.forEach((ticket) => {
      if (!(ticket.status in result)) {
        result[ticket.status] = [];
      }
      const ticketType = TicketTypeHelper.getTicketTypeFromSi(
        ticket.si,
      );
      if (!result[ticket.status].includes(ticketType)) {
        result[ticket.status].push(ticketType);
      }
    });
    return result;
  }

  static _groupTicketsByStatusTicketType(
    tickets: AllWorkdeskTicketsI,
  ): {
    [status: string]: {
      [ticketType: string]: AllWorkdeskTicketsI;
    };
  } {
    const result = {};
    tickets.forEach((ticket) => {
      if (!(ticket.status in result)) {
        result[ticket.status] = {};
      }
      const ticketType = TicketTypeHelper.getTicketTypeFromSi(
        ticket.si,
      );
      if (!(ticketType in result[ticket.status])) {
        result[ticket.status][ticketType] = [];
      }
      result[ticket.status][ticketType].push(ticket);
    });
    return result;
  }

  /**
   * TODO: write test
   * @param tickets
   * @param relatedObj
   */
  static groupTicketsByStatusTicketType(
    tickets: AllWorkdeskTicketsI,
    relatedObj: RelatedObjI,
  ): {
    [status: string]: {
      [ticketType: string]: AllWorkdeskTicketsI;
    };
  } {
    let result = this._groupTicketsByStatusTicketType(tickets);
    const final = Object.entries(relatedObj).reduce(
      (result1, [status, ticketTypes]) => {
        result1[status] = ticketTypes.reduce(
          (result2, ticketType) => {
            if (!(ticketType in result2)) {
              result2[ticketType] = [];
            }
            if (!(status in result)) {
              result2[ticketType] = [];
            } else if (!(ticketType in result[status])) {
              result2[ticketType] = [];
            } else {
              result2[ticketType] = result2[ticketType].concat(
                result[status][ticketType],
              );
            }

            return result2;
          },
          {},
        );
        return result1;
      },
      {},
    );
    console.debug(`statusTicketTypeLatestUpdateDate: 1`, {
      relatedObj,
      final,
    });
    return final;
  }

  static getLatestUpdatedAtOfTicketsListByStatusTicketType(
    tickets: AllWorkdeskTicketsI,
    oldAllInboxesStatusLatestUpdateDate: {
      status: StatusI;
      ticketType: TicketType;
      latestUpdateDate: string | null;
    }[],
    relatedObj: RelatedObjI,
  ): {
    [status: string]: { [ticketType: string]: string | null };
  } {
    const statusTicketTypeToTickets = WorkdeskTickets.groupTicketsByStatusTicketType(
      tickets,
      relatedObj,
    );

    const final = Object.entries(statusTicketTypeToTickets).reduce(
      (result, [status, statusV]) => ({
        ...result,
        [status]: Object.entries(statusV).reduce(
          (acc, [ticketType, ticketTypeV]) => ({
            ...acc,
            [ticketType]: WorkdeskTickets.getLastestUpdatedAtOfTicketsList(
              ticketTypeV, // could be empty
              oldAllInboxesStatusLatestUpdateDate.find(
                (item) =>
                  item.status === status &&
                  item.ticketType === ticketType,
              )?.latestUpdateDate ?? null,
            ),
          }),
          {},
        ),
      }),
      {},
    );
    return final;
  }

  static getLastestUpdatedAtOfTicketsList(
    tickets: AllWorkdeskTicketsI,
    oldLatestUpdateDate: string | null,
  ): string {
    // TODO: check if need to be utc
    let now = moment().format();
    if (!tickets || !tickets.length) return now;
    // updateDate is utc string
    let minTime = tickets[0].updateDate;
    const newTime = tickets.reduce((newMinTime, ticket) => {
      return moment(newMinTime).diff(moment(ticket.updateDate)) > 0
        ? newMinTime
        : ticket.updateDate;
    }, minTime) as string;

    if (!oldLatestUpdateDate) return newTime;
    else if (moment(oldLatestUpdateDate).isBefore(moment(newTime))) {
      // new time is newer
      return newTime;
    } else {
      // old time is newer
      return oldLatestUpdateDate;
    }
  }

  /**
   * is called by _getSortByHasloadedForEachSortByHasLoaded
   * @param tickets
   * @param almostLoadParams
   */
  getSortedMatchedTickets(
    tickets: AllWorkdeskTicketsI | undefined,
    almostLoadParams: {
      inboxes: string[];
      sortBy: EachSortBy[];
      status: string;
      ticketType: TicketType;
    },
  ) {
    console.time('** TIME ** getSortedMatchedTickets');
    const { inboxes, sortBy, status, ticketType } = almostLoadParams;
    // debug: it won't be good if sort a lot of tickets every time?
    const ticketsToUse =
      tickets ||
      (this.ticketsMap ? Object.values(this.ticketsMap) : []);
    const filtered = ticketsToUse.filter(
      (ticket: EachWorkdeskTicketI) =>
        this._ticketMatchedSi(ticket, ticketType) &&
        WorkdeskTickets.ticketMatchedInboxes(ticket, inboxes) &&
        this._ticketMatchedStatus(ticket, status),
    );

    console.debug(`size that have to sort ${filtered.length}`);
    let sorted = [...filtered];
    sortBy.forEach((sortByItem: EachSortBy) => {
      sorted.sort((a, b) => {
        const first = a;
        const sec = b;
        const firstAtField = first[sortByItem.field];
        const secAtField = sec[sortByItem.field];
        const multiplier = sortByItem.dir === 'DESC' ? -1 : 1;
        let toConsider;
        // TODO: combine with the other sort function
        if (
          ['receivedTime', 'updateDate'].includes(sortByItem.field)
        ) {
          toConsider =
            multiplier *
            moment(firstAtField).diff(moment(secAtField));
        } else {
          // console.warn(
          //   `do not have specific sort function for ${sortByItem.field}`,
          // );
          /* null will always come later */
          toConsider = multiplier * (firstAtField - secAtField);
        }
        return (
          (firstAtField === null ? 1 : 0) -
            (secAtField === null ? 1 : 0) || toConsider
        );
      });
    });

    console.timeEnd('** TIME ** getSortedMatchedTickets');
    return sorted;
  }

  _ticketMatchedSi(ticket: EachWorkdeskTicketI, ticketType: string) {
    return MatchHelper.ticketMatchedSi(ticket, ticketType);
  }

  /**
   * match some -> match
   * @param ticket
   * @param inboxes
   */
  static ticketMatchedInboxes(
    ticket: EachWorkdeskTicketI,
    inboxes: string[],
  ) {
    return MatchHelper.ticketMatchedInboxes(ticket, inboxes);
  }

  _ticketMatchedStatus(ticket: EachWorkdeskTicketI, status: string) {
    return MatchHelper.ticketMatchedStatus(ticket, status);
  }

  static deduplicateIds(ticketIds: string[]) {
    return Array.from(new Set(ticketIds));
  }

  static getUniqueTicketTypesOfTickets(
    tickets: EachWorkdeskTicketI[],
  ): TicketType[] {
    const allSis = tickets.map((ticket) => ticket.si);
    if (allSis.find((si) => si === undefined)) {
      console.error(`there are tickets with no ticketType`);
    }
    const uniqueAllSis = Array.from(new Set(allSis)).filter(
      (si) => si !== undefined,
    );
    return uniqueAllSis.map((si) =>
      TicketTypeHelper.getTicketTypeFromSi(si),
    );
  }

  mergeUpdatedTicketsAndHasLoaded(
    tickets: AllWorkdeskTicketsI,
    loadParams: LoadWorkdeskTicketsParams,
    statusCount?: StatusCountI,
    fromWsAt?: string, // will use in moveTicket which will not use current workdeskFilter to determine the ticket's position
    fromScroll = false,
  ): {
    ticketsMap: { [id: string]: EachWorkdeskTicketI };
    hasLoaded: StoreHasLoadedI;
    newTicketIds: string[];
    statusChangedIds: string[];
  } {
    const {
      ticketsMap,
      newTicketIds,
      statusChangedIds,
    } = this.mergeTickets(tickets, fromWsAt);

    console.debug(
      `** call in mergeUpdatedTicketsAndHasLoaded`,
      'loadParams',
      loadParams,
      'tickets',
      tickets,
      'ticketsMap',
      ticketsMap,
      'statusChangedIds',
      statusChangedIds,
    );
    const newModel = new WorkdeskTickets(
      ticketsMap,
      this.assigneeIdToInfo,
      this.hasLoaded,
    );
    const hasLoaded = newModel.moveTicketsInHasLoaded(
      tickets,
      loadParams,
      !!fromWsAt || false,
      statusCount,
      fromScroll,
    );
    return {
      ticketsMap,
      hasLoaded,
      newTicketIds,
      statusChangedIds,
    };
  }
  moveTicketsInHasLoaded(
    ticketsInput: AllWorkdeskTicketsI,
    loadParams: LoadWorkdeskTicketsParams,
    fromWs = false,
    statusCount?: StatusCountI,
    fromScroll = false,
  ): StoreHasLoadedI {
    const ticketIds = ticketsInput.map((ticket) => ticket.id);
    const tickets = ticketsInput.map((ticket) => ({
      ...((this.ticketsMap && this.ticketsMap[ticket.id]) || {}),
      ...ticket,
    }));
    let newHasLoaded: StoreHasLoadedI = { ...this.hasLoaded };

    /**
     * don't remove if from scroll
     */
    if (!fromScroll) {
      const removeTheIdHasLoaded = HasLoadedHelper.getRemovedIdsHasLoaded(
        this.hasLoaded,
        ticketIds,
      );
      newHasLoaded = removeTheIdHasLoaded;
    }

    newHasLoaded = new HasLoadedHelper(
      newHasLoaded,
      this.ticketsMap,
    ).moveTickets(
      tickets,
      loadParams,
      fromWs,
      statusCount,
      fromScroll,
    );

    return newHasLoaded;
  }

  /**
   * if hasLoaded is not passed it will always return loadParams even if already loaded data
   * if it is passed, the function will check if has already loaded, if so it will return null so the app won't load the same data twice
   * if starts with search then clear, when clear will load (return params)
   * @param workdeskFilter
   * @param hasLoaded
   */
  static workdeskFilterToLoadParams(
    workdeskFilterWithPrev: WorkdeskFilterWithPrevStoreState,
    hasLoaded?: StoreHasLoadedI, // if needs hasLoaded because getLoadParamsForEachStatus won't return new params if already loaded
    statusChangedIds?: string[], // if has loaded is passed must have ticketsMap
    justChangedAndFromWsTicketIds?: string[],
  ): null | LoadWorkdeskTicketsParams {
    if (
      hasLoaded &&
      (!statusChangedIds || !justChangedAndFromWsTicketIds)
    ) {
      throw Error(
        `if has loaded is passed must have statusChangedIds and justChangedAndFromWsTicketIds`,
      );
    }

    const {
      selectedInboxes,
      statusesStartAt,
      selectedTicketType,
      selectedStatus: status,
      sortBy,
      searchValues,
    } = workdeskFilterWithPrev.cur;
    if (!status) {
      console.debug(`no params: no status`);
      return null;
    }
    const startAt =
      (statusesStartAt[status] &&
        statusesStartAt[status][selectedTicketType]) ||
      0;
    const inboxes = getInboxesFromSelectedInboxes(selectedInboxes);
    if (!inboxes || !inboxes.length) {
      console.debug(`no params: no inboxes`);
      return null;
    }

    const preMaturedloadParams = {
      inboxes,
      status,
      ticketType: selectedTicketType,
      sortBy,
      search: SearchFilter.storeStateToApi(searchValues),
    };

    /*
    if hasLoaded is passed, the function will check if has already loaded, if so it will return null so the app won't load the same data twice
    */
    if (hasLoaded !== undefined) {
      // check if in hasLoaded
      const anyHasLoaded = WorkdeskTickets.getMatchedHasLoaded(
        preMaturedloadParams,
        hasLoaded,
      );

      let allChangedIds: string[] = [];
      if (statusChangedIds) {
        allChangedIds = allChangedIds.concat(statusChangedIds);
      }
      if (justChangedAndFromWsTicketIds) {
        allChangedIds = allChangedIds.concat(
          justChangedAndFromWsTicketIds,
        );
      }
      if (allChangedIds.length) {
        allChangedIds = WorkdeskTickets.deduplicateIds(allChangedIds);
      }

      // NOTE: maybe change to filter the just changed
      const filteredAllChangedIds =
        (anyHasLoaded &&
          allChangedIds &&
          anyHasLoaded.ticketIds.filter(
            (id) => !allChangedIds?.includes(id),
          )) ||
        [];

      // have to conver the case where tickets got moved from other statuses (after assigned for example)
      const hasSearchValuesChanged = SearchFilter.hasSearchValuesChanged(
        workdeskFilterWithPrev?.cur?.searchValues,
        workdeskFilterWithPrev?.prev?.searchValues,
      );
      const shouldNotLoad =
        (anyHasLoaded && !!anyHasLoaded.hasGotAllResults) ||
        (!!(anyHasLoaded && filteredAllChangedIds.length > startAt) &&
          !hasSearchValuesChanged);
      console.debug(
        `check hasLoaded. startAt: ${startAt}`,
        'ticketIds ',
        anyHasLoaded?.ticketIds?.length,
        'filteredAllChangedIds ',
        filteredAllChangedIds?.length,
        'shouldNotLoad',
        shouldNotLoad,
        'workdeskFilterWithPrev',
        workdeskFilterWithPrev,
      );

      if (shouldNotLoad) {
        // use the cached data if already has loaded
        console.info(
          `already have loaded. startAt: ${startAt}`,
          'anyHasLoaded: ',
          anyHasLoaded,
          'loadParams: ',
          preMaturedloadParams,
          'ticketIds ',
          anyHasLoaded?.ticketIds?.length,
          'filteredAllChangedIds ',
          filteredAllChangedIds?.length,
        );
        return null;
      }
    }

    const loadParams = {
      ...preMaturedloadParams,
      startAt,
      rowsPerPage: ROWS_PER_STATUS,
      // sortDir: 'DESC' as SortDirType,
    };
    return loadParams;
  }

  /**
   * TODO: might want to refactor this function
   * @param preMaturedloadParams
   * @param hasLoaded
   */
  static getMatchedHasLoaded(
    preMaturedloadParams: {
      inboxes: string[];
      status: string;
      ticketType: TicketType;
      sortBy: EachSortBy[];
      startAt?: number;
      rowsPerPage?: number;
    },
    hasLoaded: StoreHasLoadedI,
  ): null | EachHasLoaded {
    const {
      status,
      inboxes,
      ticketType,
      sortBy,
    } = preMaturedloadParams;
    if (!sortBy)
      throw Error(`missing sortBy in preMaturedloadParams`);
    if (!hasLoaded[status]) return null;
    else if (hasLoaded[status] && !hasLoaded[status][ticketType])
      return null;
    console.time('** TIME ** getMatchedHasLoaded');
    // TODO
    const matchedStatusAndStatysType = hasLoaded[status][ticketType];
    const matchedInboxes = matchedStatusAndStatysType.filter((each) =>
      MatchHelper.matchedExactInboxes(inboxes, each.inboxes),
    );
    if (!matchedInboxes.length) {
      console.timeEnd('** TIME ** getMatchedHasLoaded');
      return null;
    }

    // matched sortBy
    let clonedMatchedInboxes = [...matchedInboxes].sort(
      (a, b) => a.inboxes.length - b.inboxes.length,
    );
    const matchedSortBy = clonedMatchedInboxes
      .map((each) =>
        each.sortByHasLoaded.find((sH) =>
          MatchHelper.matchedExactSortByArray(sH.sortBy, sortBy),
        ),
      )
      .filter((sb) => sb !== undefined);

    if (!matchedSortBy.length) {
      console.timeEnd('** TIME ** getMatchedHasLoaded');
      return null;
    }

    if (matchedSortBy.length > 1) {
      console.error(
        '> 1 matchedSortBy',
        matchedSortBy,
        'status',
        status,
        'ticketType',
        ticketType,
        'matchedStatusAndStatysType',
        matchedStatusAndStatysType,
      );
    }
    const matched = matchedSortBy[0]
      ? matchedSortBy[0].hasLoaded
      : null;
    console.timeEnd('** TIME ** getMatchedHasLoaded');
    return matched;
  }

  static getFilteredSearchMatchedHasLoaded(
    preMaturedloadParams: {
      inboxes: string[];
      status: string;
      ticketType: TicketType;
      sortBy: EachSortBy[];
      startAt?: number;
      rowsPerPage?: number;
      searchValues?: SearchValuesApi;
    },
    hasLoaded: StoreHasLoadedI,
    ticketsMap: TicketsMap,
  ): null | EachHasLoaded {
    const matchedHasLoaded = WorkdeskTickets.getMatchedHasLoaded(
      preMaturedloadParams,
      hasLoaded,
    );
    if (!matchedHasLoaded) {
      return matchedHasLoaded;
    }
    if (
      !SearchFilter.hasAnySearchValues(
        preMaturedloadParams.searchValues,
      )
    ) {
      return matchedHasLoaded;
    }
    return {
      ...matchedHasLoaded,
      ticketIds: matchedHasLoaded.ticketIds.filter((id) => {
        const ticket = ticketsMap[id];
        if (!ticket) return false;
        // matched search condition here
        return SearchFilter.isTicketMatchedStoreStateSearchValues(
          ticket,
          preMaturedloadParams.searchValues,
        );
      }),
    };
  }
}
